<template>
  <div class="h-full flex flex-col overflow-hidden">

    <!-- Header -->
    <div class="px-2 py-1.5 text-xs uppercase tracking-widest text-gray-500 border-b border-gray-700 shrink-0 flex items-center gap-1.5 min-w-0">
      <span class="shrink-0">Explorer</span>
      <span v-if="repoStore.activeRepo" class="text-gray-600 normal-case tracking-normal truncate">
        — {{ repoStore.activeRepo.name }}
      </span>

      <!-- Option B: new file / new folder buttons in header -->
      <template v-if="repoStore.activeRepo">
        <div class="ml-auto flex items-center shrink-0 gap-0.5">
          <button
            class="px-1 py-0.5 rounded text-gray-500 hover:text-gray-200 hover:bg-gray-700/60 leading-none transition-colors"
            title="New File in repository root"
            @click="openNewItemDialog('file')"
          >+📄</button>
          <button
            class="px-1 py-0.5 rounded text-gray-500 hover:text-gray-200 hover:bg-gray-700/60 leading-none transition-colors"
            title="New Folder in repository root"
            @click="openNewItemDialog('dir')"
          >+📁</button>
        </div>
      </template>
    </div>

    <!-- Option B: inline input bar shown below header -->
    <div
      v-if="newItemDialog.visible"
      class="px-2 py-1 border-b border-gray-700 shrink-0 flex items-center gap-1.5"
    >
      <span class="text-xs shrink-0">{{ newItemDialog.type === 'dir' ? '📁' : '📄' }}</span>
      <input
        ref="newItemInputEl"
        v-model="newItemDialog.name"
        :placeholder="newItemDialog.type === 'dir' ? 'new-folder' : 'new-file.ts'"
        class="flex-1 min-w-0 bg-gray-900 text-gray-100 text-xs border border-blue-500 rounded px-1.5 py-0.5 outline-none"
        @keydown.enter.prevent="confirmNewItem"
        @keydown.esc="cancelNewItem"
        @blur="cancelNewItem"
      />
      <span class="text-gray-600 text-[10px] shrink-0">↵</span>
    </div>

    <!-- Tree (only rendered when a repo is active) -->
    <div
      v-if="repoStore.activeRepo"
      class="flex-1 overflow-y-auto py-1"
    >
      <!--
        :key forces a full unmount + remount of the tree whenever the active
        repository changes. Without it, the FileTree component would keep its
        old items because onMounted only fires once per component lifetime.
        treeKey increments when a file/folder is created via the header buttons.
      -->
      <FileTree
        :key="`${repoStore.activeRepo.id}:${gitStore.currentBranch}:${treeKey}`"
        :path="repoStore.activeRepoRootPath"
      />
    </div>

    <!-- Empty state while no repo is selected -->
    <div
      v-else
      class="flex-1 flex items-center justify-center text-gray-600 text-xs"
    >
      No repository selected
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick } from 'vue'
import FileTree from './FileTree.vue'
import { useRepoStore } from '@/stores/repoStore'
import { useGitStore }  from '@/stores/gitStore'
import { createFile, createFolder } from '@/services/fileService'

const repoStore = useRepoStore()
const gitStore  = useGitStore()

const treeKey        = ref(0)
const newItemInputEl = ref<HTMLInputElement | null>(null)

const newItemDialog = reactive({
  visible: false,
  type:    'file' as 'file' | 'dir',
  name:    '',
})

function openNewItemDialog(type: 'file' | 'dir'): void {
  newItemDialog.visible = true
  newItemDialog.type    = type
  newItemDialog.name    = ''
  nextTick(() => newItemInputEl.value?.focus())
}

async function confirmNewItem(): Promise<void> {
  const name = newItemDialog.name.trim()
  if (!name) { cancelNewItem(); return }
  const rootPath = repoStore.activeRepoRootPath
  if (!rootPath) { cancelNewItem(); return }
  const fullPath = rootPath.replace(/\/$/, '') + '/' + name
  const type     = newItemDialog.type
  cancelNewItem()
  try {
    if (type === 'dir') {
      await createFolder(fullPath)
    } else {
      await createFile(fullPath)
    }
    treeKey.value++
  } catch (err) {
    console.error('[FileExplorer] create failed:', String(err))
  }
}

function cancelNewItem(): void {
  newItemDialog.visible = false
  newItemDialog.name    = ''
}
</script>
